import { Transactions } from '../../../models/transactions.model';
import { addMonths } from 'date-fns';
import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { MyAccountActivityIcon } from './MyAccountActivityIcons';
import { MyAccountActivityPanel } from './MyAccountActivityPanel';
import { MyAccountActivityContent } from './MyAccountActivityContent';
import { MyAccountActivityProps } from './types';
import { DateRange, DateRangePayload } from '../../shared/DateRange';
import { Heading } from '../../shared/Heading';
import { TabularDatalist } from '../../shared/TabularDatalist';
import { useSnackbar } from '../../../hooks/snackbar';
import { AccountPageWrapper } from '../MyAccount/AccountPageWrapper';
import { TabularDataDetailsItem } from '../../shared/TabularDatalist/TabularDataDetailsItem';
import { useUserContext } from '../../../providers/UserProvider';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useUserTransactionsLazyQuery } from '../../../libs/graphql/baseAppAPI/queries/__generated__/user-transactions.query.generated';
import { xTenantId } from '../../../consts';
import { walletTypeNames } from './consts';
import { Paragraph } from '../../shared/Paragraph';
import './styles';
import { Chip } from '../../shared/Chip';
import { Icon } from '../../shared/Icon';

export const MyAccountActivity: FC<MyAccountActivityProps> = (props) => {
  const { t } = useTranslations();
  const { addSnack } = useSnackbar();
  const { userProfileId } = useUserContext();
  const lastItemRef = useRef<HTMLDivElement>(null);

  const [allTransactions, setAllTransactions] = useState<Transactions>([]);
  const [endCursor, setEndCursor] = useState<string>('');
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  type ActivityStatus = 'loadingInitial' | 'showing' | 'loadingMore' | 'completed' | 'empty' | 'error';
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>('loadingInitial');

  const [dateRange, setDateRange] = useState({
    startDate: addMonths(new Date(), -1),
    endDate: new Date(),
  });

  const [mediatorTypeFilter, setMediatorTypeFilter] = useState<string>('');

  const [fetchTransactions, { data: transactionsData, loading: transactionsLoading, error: transactionsError }] =
    useUserTransactionsLazyQuery({
      variables: {
        inTenantId: xTenantId,
        inUserProfileId: userProfileId,
        inDatefromEpoch: dateRange.startDate.setHours(0, 0, 0, 0),
        inDatetoEpoch: dateRange.endDate.setHours(23, 59, 59, 999),
        after: endCursor,
        first: 50,
      },
      fetchPolicy: 'network-only',
    });

  //MERGE NEW TRANSACTIONS INTO STATE
  const handleTransactions = (newTransactions: Transactions): void => {
    if (!newTransactions?.length) return;

    setAllTransactions((prevTransactions) => {
      const updatedTransactions = [...prevTransactions];

      newTransactions.forEach((transaction) => {
        if (!updatedTransactions.some((t) => t?.transactionId === transaction?.transactionId)) {
          updatedTransactions.push(transaction);
        }
      });

      // Update the master list
      return updatedTransactions;
    });
  };

  //FILTER TRANSACTIONS BY AMOUNT AND MEDIATOR TYPE
  const resultTransactions = useMemo(() => {
    return allTransactions.filter((transaction) => {
      const hasAmount = transaction?.transactionAmount;
      const matchesMediator = mediatorTypeFilter === '' || transaction?.mediatorType === mediatorTypeFilter;

      return hasAmount && matchesMediator;
    });
  }, [allTransactions, mediatorTypeFilter]);

  const totalTransactions = resultTransactions?.length || 0;

  //RESET TRANSACTIONS AND PAGINATION
  const clearTransactions = (): void => {
    setAllTransactions([]);
    setEndCursor('');
    setHasNextPage(false);
  };

  //SET DATE RANGE THEN TRIGGER RESET AND LOAD
  const assignDate = ({ startDate, endDate }: DateRangePayload): void => {
    if (startDate && endDate) {
      clearTransactions();
      setDateRange({ startDate, endDate });
      setActivityStatus('loadingInitial');
    }
  };

  //FETCH TRANSACTIONS WHEN ACTIVITY STATUS IS LOADING
  useEffect(() => {
    if (activityStatus === 'loadingInitial' || activityStatus === 'loadingMore') {
      fetchTransactions();
    }
  }, [activityStatus, fetchTransactions, dateRange, endCursor]);

  //HANDLE NEW TRANSACTIONS DATA
  useEffect(() => {
    const page = transactionsData?.userTransactions;

    if (!page) return;

    const cleanTransactions = page.edges.map((e) => e.node);
    const hasNext = Boolean(page.pageInfo.hasNextPage);

    setHasNextPage(hasNext);
    handleTransactions(cleanTransactions);

    //CALCULATE TOTAL AFTER MERGING AND FILTERING
    const nextFilteredTotal = ((): number => {
      const merged = [...allTransactions];

      cleanTransactions.forEach((tx) => {
        if (!merged.some((t) => t?.transactionId === tx?.transactionId)) merged.push(tx);
      });

      return merged.filter((tx) => {
        const hasAmount = tx?.transactionAmount;
        const matchesMediator = mediatorTypeFilter === '' || tx?.mediatorType === mediatorTypeFilter;

        return hasAmount && matchesMediator;
      }).length;
    })();

    //UPDATE ACTIVITY STATUS BASED ON RESULTS AND PAGINATION
    if (nextFilteredTotal === 0 && !hasNext) {
      setActivityStatus('empty');
    } else if (!hasNext && nextFilteredTotal > 0) {
      setActivityStatus('completed');
    } else {
      setActivityStatus('showing');
    }
  }, [transactionsData, mediatorTypeFilter]);

  //HANDLE ERROR FROM QUERY
  useEffect(() => {
    if (transactionsError) {
      addSnack({
        type: 'error',
        message: t('my-account.home.my-activity.transaction-handling-error'),
      });
      setActivityStatus('error');
    }
  }, [transactionsError, addSnack, t]);

  //LOAD NEXT PAGE AND UPDATE CURSOR
  const refetchTransactions = (): void => {
    if (transactionsLoading || !hasNextPage) return;
    setActivityStatus('loadingMore');
    setEndCursor(transactionsData?.userTransactions?.pageInfo.endCursor ?? '');
  };

  //ALLOWS INFINITE SCROLL, OBSERVE THE LAST ITEM AND TRIGGER REFETCH WHEN IT IS VISIBLE
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !transactionsLoading) {
          refetchTransactions();
        }
      },
      { threshold: 0.1 }
    );
    const el = lastItemRef.current;

    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [hasNextPage, transactionsLoading, resultTransactions]);

  const isLoading =
    activityStatus === 'loadingInitial' || activityStatus === 'loadingMore' || activityStatus === 'showing';
  const showDataset =
    activityStatus === 'showing' || activityStatus === 'loadingMore' || activityStatus === 'completed';
  const showNoResultsMessage = activityStatus === 'empty';
  const showLimitWarning = activityStatus === 'completed' && totalTransactions > 0;

  const tabularDataset:
    | { content: ReactElement<string, string> | null; panel: ReactElement<string, string> }[]
    | undefined = useMemo(
    () =>
      resultTransactions?.map((transaction) => {
        const {
          previousBalanceAmount,
          balanceAmount,
          transactionAmount,
          transactionStatus,
          lastUpdatedOn,
          // createdOn,
          walletType,
          mediatorType,
          operationType,
          mediatorId,
        } = transaction || {};

        const payout = Math.abs(Number(previousBalanceAmount) - Number(balanceAmount));

        const description = `${t(
          walletType === walletTypeNames.main ? 'my-account.cache-wallet' : 'my-account.bonus-wallet'
        )} - ${mediatorType?.replace(/^./, (str) => str.toUpperCase())}`;

        const getTransactionTitle = (operationType: string, mediatorType: string, mediatorId: string): string => {
          // TODO: use translations
          if (mediatorId === 'endeavour-manual-adjustments') {
            return 'Manual Adjustment';
          }

          if (operationType === 'Credit') {
            switch (mediatorType.toLowerCase()) {
              case 'sports':
                return 'Bet Settlement';
              case 'casino':
                return (
                  transaction?.additionalProperties?.Casino?.GameName || transaction?.additionalProperties?.Note || ''
                );
              case 'monetary':
                return 'Deposit';
              default:
                return 'Account Credit';
            }
          } else if (operationType === 'Debit') {
            switch (mediatorType.toLowerCase()) {
              case 'sports':
                return transaction?.additionalProperties?.Sports?.Transaction.BetType === 'Combined'
                  ? 'Combined Bet'
                  : (transaction?.additionalProperties?.Sports?.Selections[0].EventName as string);
              case 'casino':
                return (
                  transaction?.additionalProperties?.Casino?.GameName || transaction?.additionalProperties?.Note || ''
                );
              case 'monetary':
                return 'Withdrawal';
              default:
                return 'Account Debit';
            }
          }

          return 'Transaction';
        };

        const transactionTitle = getTransactionTitle(String(operationType), String(mediatorType), String(mediatorId));

        return {
          content: transaction?.additionalProperties?.Sports?.Selections ? (
            <MyAccountActivityContent
              description={description}
              couponRef={String(transaction?.transactionId)}
              transactionAmount={Number(transactionAmount)}
              content={
                <>
                  {transaction.additionalProperties.Sports.Selections.map((selection, index) => (
                    <TabularDataDetailsItem
                      {...props}
                      key={`${transaction?.transactionId}-${index}`}
                      eventStartDate={selection.EventStartDate}
                      title={{
                        text: selection.SelectionName,
                        odds: selection.Odds,
                      }}
                      subtitle={selection.SportName}
                      content={`${selection.EventName} - ${selection.MarketName}`}
                      stake={Math.abs(Number(transactionAmount))}
                      payout={Number((Math.abs(Number(transactionAmount)) * Number(selection.Odds)).toFixed(2))}
                      marker={index + 1}
                      isSingleBet={transaction?.additionalProperties?.Sports?.Transaction.BetType === 'Single'}
                    />
                  ))}
                </>
              }
              payout={payout}
              isSingleBet={transaction.additionalProperties.Sports.Transaction.BetType === 'Single'}
            />
          ) : null,
          panel: (
            <MyAccountActivityPanel
              title={transactionTitle}
              status={String(transactionStatus)}
              amount={Number(transactionAmount)}
              transactionDate={String(lastUpdatedOn)}
              cashBalanceAfter={balanceAmount}
              walletType={String(walletType)}
              ActivityIcon={
                <MyAccountActivityIcon operationType={String(operationType)} mediatorType={String(mediatorType)} />
              }
            />
          ),
        };
      }),
    [resultTransactions]
  );

  return (
    <>
      <AccountPageWrapper {...props} className="my-activity">
        <div className="my-account-activity__heading">
          <Heading level={3}>{t('my-account.my-activity')}</Heading>

          <div className="my-account-activity__daterange">
            <DateRange
              dateFormat="dd/MMM/yyyy"
              startDateTitle={t('my-account.home.my-activity.start-date')}
              endDateTitle={t('my-account.home.my-activity.end-date')}
              startingDate={dateRange.startDate}
              endingDate={dateRange.endDate}
              onAction={assignDate}
              isCustomHeader
              inputBoxClassName="my-account-activity__date-range"
            />
          </div>

          <div className="my-account-activity__chips">
            <Chip
              variant="rounded"
              className={mediatorTypeFilter === 'monetary' ? 'active' : ''}
              onClick={(): void => {
                setMediatorTypeFilter(mediatorTypeFilter !== 'monetary' ? 'monetary' : '');
              }}
            >
              <Icon variant="IoWalletOutline" />
              Banking
            </Chip>
            <Chip
              variant="rounded"
              className={mediatorTypeFilter === 'sports' ? 'active' : ''}
              onClick={(): void => {
                setMediatorTypeFilter(mediatorTypeFilter !== 'sports' ? 'sports' : '');
              }}
            >
              <Icon variant="IoIosFootball" />
              Sports
            </Chip>
            <Chip
              variant="rounded"
              className={mediatorTypeFilter === 'casino' ? 'active' : ''}
              onClick={(): void => {
                setMediatorTypeFilter(mediatorTypeFilter !== 'casino' ? 'casino' : '');
              }}
            >
              <Icon variant="IoDiceOutline" />
              Casino
            </Chip>
          </div>
        </div>

        <div className="my-account-activity__content">
          <div className="my-account-activity__data">
            <div className="my-account-activity__transactions-wrapper">
              <TabularDatalist
                showCount
                isLoading={isLoading}
                onLoadMore={refetchTransactions}
                showLoadMore={false}
                totalResults={totalTransactions}
                dataset={showDataset ? tabularDataset : undefined}
                showNoResultsMessage={showNoResultsMessage}
              />
              {/* Invisible element to detect when we're at the bottom of the list */}
              {hasNextPage && <div ref={lastItemRef} style={{ height: '10px', width: '100%', opacity: 0 }} />}
            </div>

            {showLimitWarning && (
              <Paragraph className="my-account-activity__limit-warning">
                {t('my-account.home.my-activity.history.limit')}
              </Paragraph>
            )}
          </div>
        </div>
      </AccountPageWrapper>
    </>
  );
};
